/**
 * MobileShell — the phone-sized app frame.
 *
 * Rendered INSTEAD of the desktop sidebar/topbar shell when
 * useMobileExperience() is true. It renders the exact same routed `children`
 * (the main pages, with their route guards and permission checks), wrapped in
 * mobile chrome:
 *   - fixed top header (theme, Guides, notification bell) + bottom tab bar
 *   - quick-capture FAB
 *   - "More" navigation drawer
 *   - command palette (search) + global notifications + toasts
 *
 * One page per address (roadmap M-7, 2026-10-29): the separate phone copies of
 * cycle, track, session, intake, Catchball, strategy, settings and home pages
 * had fallen behind their main pages (old wording, missing states, skipped
 * route guards, a second item-insert path) and were removed; phones open the
 * main page inside this frame. The item list and the 13 prioritization tools
 * followed (roadmap M-7, 2026-10-30): nothing swaps in a phone copy any more.
 *
 * Everything here lives under src/mobile/** and is gated by one conditional in
 * Layout.tsx — delete the dir + revert that conditional to fully roll back.
 */
import { type ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './mobile.css';
import { ToastProvider } from '../components/ui/Toast';
import { NotificationToast } from '../components/NotificationToast';
import { CommandPalette } from '../components/prioritization/CommandPalette';
import { HarryShell } from '../components/harry/HarryShell';
import { MobileHeader } from './MobileHeader';
import { MobileTabBar } from './MobileTabBar';
import { MobileTabSubmenu } from './MobileTabSubmenu';
import { MobileDrawer } from './MobileDrawer';
import { MobileAdminLeaf } from './MobileAdminLeaf';
import { isSessionPresentPath, isSessionTvPath } from './mobileSessionRoutes';
import {
  isSettingsLeafPath,
  isStrategyLeafPath,
  parseCycleAdminLeafPath,
} from './mobileRouteCoverage';
import {
  isPrioritizationTopLevelPath,
  roleMenuDrawerSections,
  roleMenuTabItems,
  type MobileNavItem,
} from './navConfig';
import { useContributorExperience, useRoleMenu } from '../hooks/prioritization/useContributorExperience';

interface MobileShellProps {
  children: ReactNode;
}

/**
 * Match /prioritization/sessions/<id>/participate — rendered fullscreen
 * (no mobile chrome) because the participant flow has its own dark design system.
 */
const SESSION_PARTICIPATE_RE = /^\/prioritization\/sessions\/[^/]+\/participate/;

/**
 * Match /prioritization/sessions/<id>/live — rendered fullscreen.
 * LiveSessionViewport is fixed-positioned and owns its own chrome.
 */
const SESSION_LIVE_RE = /^\/prioritization\/sessions\/[^/]+\/live/;

export function MobileShell({ children }: MobileShellProps) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tabSubmenu, setTabSubmenu] = useState<MobileNavItem | null>(null);
  // Role-based menu (R-7): a member on the short menu gets the same links on the
  // phone as on the desktop — both are drawn from buildRoleMenu.
  const { useContributorShell } = useContributorExperience();
  const roleMenu = useRoleMenu();

  // An item opens the same item page as on a computer, inside the phone frame
  // (2026-10-15: the separate phone copy had fallen behind and was removed).
  const isParticipate = SESSION_PARTICIPATE_RE.test(location.pathname);
  const isLive = SESSION_LIVE_RE.test(location.pathname);
  const isPresent = isSessionPresentPath(location.pathname);
  const isTv = isSessionTvPath(location.pathname);
  const isSettingsLeaf = isSettingsLeafPath(location.pathname);
  const cycleAdminLeaf = parseCycleAdminLeafPath(location.pathname);
  const isStrategyLeaf = isStrategyLeafPath(location.pathname);

  // Participate, live, and TV routes own their fullscreen design — skip mobile chrome.
  if (isParticipate || isLive || isPresent || isTv) {
    return <ToastProvider>{children}</ToastProvider>;
  }

  // The header is on every page: the bell, theme and Guides live in it (audit #35).
  const topLevel = isPrioritizationTopLevelPath(location.pathname);
  const mainPaddingTop = 'calc(var(--pa-header-h) + var(--pa-safe-top))';

  return (
    <ToastProvider>
      {/* Guides open from the header; the floating launcher would sit on the tab bar. */}
      <HarryShell suppressLauncher>
      <div className="pa-mobile min-h-screen bg-white dark:bg-[var(--bg)] text-gray-900 dark:text-[var(--text-pri)]">
        <NotificationToast />
        <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

        <MobileHeader topLevel={topLevel} />

        {/* Scrollable content between the fixed header and the tab bar */}
        <main
          className="pa-scroll"
          style={{
            paddingTop: mainPaddingTop,
            paddingBottom: 'calc(var(--pa-tabbar-h) + var(--pa-safe-bottom))',
            minHeight: '100vh',
          }}
        >
          {/* Wide desktop pages; the header's Back goes to their parent (mobileBackTarget). */}
          {cycleAdminLeaf || isStrategyLeaf || isSettingsLeaf ? (
            <MobileAdminLeaf>{children}</MobileAdminLeaf>
          ) : (
            // The phone's version of the desktop frame's px-6 page gutter.
            <div className="px-4 pt-3 pb-4 min-w-0">{children}</div>
          )}
        </main>

        <MobileTabBar
          items={useContributorShell ? roleMenuTabItems(roleMenu) : undefined}
          onMore={() => {
            setTabSubmenu(null);
            setDrawerOpen((open) => !open);
          }}
          onSearch={() => {
            setDrawerOpen(false);
            setTabSubmenu(null);
            setSearchOpen(true);
          }}
          onTabSubmenu={(tab) => {
            setDrawerOpen(false);
            setTabSubmenu(tab);
          }}
          moreOpen={drawerOpen}
          searchOpen={searchOpen}
        />

        <MobileTabSubmenu
          open={tabSubmenu != null}
          onClose={() => setTabSubmenu(null)}
          tab={tabSubmenu}
        />

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sections={useContributorShell ? roleMenuDrawerSections(roleMenu) : undefined}
          showProductSwitch={useContributorShell ? roleMenu.showProductSwitch : true}
          showMorePages={!useContributorShell}
          switchers={useContributorShell ? 'none' : 'workspace-and-space'}
          onSearch={() => setSearchOpen(true)}
        />
      </div>
      </HarryShell>
    </ToastProvider>
  );
}
